import {
  useFetchMovieDetailData,
  useFetchSpecificMovieData,
} from "~/composables/useFetchCraftData";
import { computed, getSiteHandle } from "#imports";

type PromisedType<TPromise> = TPromise extends PromiseLike<infer TInferredType>
  ? TInferredType
  : TPromise;

type TGlobalMovieQueryResult = PromisedType<
  ReturnType<typeof useFetchMovieDetailData>
>;

/**
 * fetches & returns movie data used to render the detail page
 */
export const useMovieDetailData = async (movieId: Ref<string>) => {
  // fetch cinema-specific movie data (cinema availability)
  const cinemaMovie = await useFetchSpecificMovieData({
    site: getSiteHandle(),
    MovieId: movieId.value,
  });

  let globalMovie: TGlobalMovieQueryResult | null = null;

  // if cinema-specific data is not available (e.g. movie is not in cinema anymore),
  // fetch global/universal movie data
  // the ID of cinema-specific movie and global movie is the same
  if (!cinemaMovie.data.value?.entry) {
    globalMovie = await useFetchMovieDetailData({ MovieId: movieId.value });
  }

  const result = globalMovie ?? cinemaMovie;

  const data = computed(() => {
    const cinemaMovieData = cinemaMovie.data.value;
    const globalMovieEntry = globalMovie?.data.value?.entry;

    // if cinema-specific movie data is available, return it
    if (cinemaMovieData?.entry) {
      return cinemaMovieData;
    }

    // if not, check if global movie data is available
    // and build cinema-movie-like response structure
    if (globalMovieEntry) {
      return {
        entry: {
          MovieId: globalMovieEntry.MovieId,
          // premiere is set only on cinema-specific data
          Premiere: null,
          PictureUrl: globalMovieEntry.PictureUrl,
          moviePoster: globalMovieEntry.moviePoster,
          TitleArray: globalMovieEntry.TitleArray,
          Title2Array: globalMovieEntry.Title2Array,
          movie: [globalMovieEntry],
          // movie group should reference the main movie if the current record is a sub-movie
          // but in this case it would require to trigger another request for sub-movies
          // so for now we use the current movie data
          // ! this leads to an issue when loading detail of a sub-movie that is no longer in cinema
          // – most of the data (e.g. description) is not available on sub-movie and the page will be mostly empty
          movieGroup: [globalMovieEntry],
        },
      };
    }

    return null;
  });

  return { ...result, data };
};
